<script setup lang="ts">
import AppInput from '@/components/UI/AppInput/AppInput.vue';
import AppSelect from '@/components/UI/AppSelect/AppSelect.vue';
import AppInputAmount from '@/components/UI/AppInputAmount/AppInputAmount.vue';
import AppDateTimePicker from '@/components/UI/AppDateTimePicker/AppDateTimePicker.vue';
import mtRadioGroup from '@/components/UI/mtRadio/mtRadioGroup.vue';
import MtButton from '@/components/UI/mtButton/index.vue';
import useToast from '@/components/UI/AppToast/useToast';
import AppAlert from '@/components/UI/AppAlert/AppAlert.vue';
import { FieldsType, rules, useValidate } from '@/services/validate.ts';
import { deepClone, validateEmail } from '@/services/utils';
import MguuInput from '@/components/UI/MguuInput.vue';
import MguuButton from '@/components/UI/MguuButton.vue';
import MguuRadioGroup from '@/components/UI/MguuRadioGroup.vue';
import AppInputPassword from '@/components/UI/AppInputPassword.vue';
import { useStore } from 'vuex';
import { computed, markRaw, ref, useSlots, watch } from 'vue';

const slots = useSlots();

interface Props {
    modelValue: Object;
    handler: (data) => Promise<any>;
    handlerButtonText?: string;
    disableButton?: boolean;
    forceMguu?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    handlerButtonText: 'Сохранить',
});
const emits = defineEmits(['update:modelValue', 'submit', 'validation-error']);
const store = useStore();
const isMguuRespondent = computed(() => {
    if (props.forceMguu) return import.meta.env.VITE_APP_DESIGN_TYPE === 'mguu';
    return (
        import.meta.env.VITE_APP_DESIGN_TYPE === 'mguu' &&
        store.getters.authUserRoleSlug === 'respondent'
    );
});

const { validate, errors } = useValidate();

const fields = ref(modifyFields());

function isRequired(key: string) {
    const values = Object.values(props.modelValue);

    for (let i = 0; i < values.length; i++) {
        const item = values[i];
        if (item.key === key && typeof item.rules?.required === 'function') {
            return true;
        }
    }

    return false;
}

function modifyFields() {
    const fields = deepClone(props.modelValue);

    Object.values(fields).forEach((field, index) => {
        const componentType = {
            string: {
                component: markRaw(
                    isMguuRespondent.value ? MguuInput : AppInput,
                ),
            },
            password: {
                component: markRaw(
                    isMguuRespondent.value ? MguuInput : AppInputPassword,
                ),
            },
            integer: {
                component: markRaw(
                    isMguuRespondent.value ? MguuInput : AppInputAmount,
                ),
            },
            date: {
                component: markRaw(
                    isMguuRespondent.value ? MguuInput : AppDateTimePicker,
                ),
                bind: {
                    format: 'dd.MM.yyyy',
                },
            },
            list: {
                component: markRaw(AppSelect),
            },
            radio: {
                component: markRaw(
                    isMguuRespondent.value ? MguuRadioGroup : mtRadioGroup,
                ),
                bind: isMguuRespondent.value ? {} : { inline: true },
            },
        };
        const type = field.type ?? 'string';
        const component = componentType[type];

        if (field.key == 'email') {
            field.rules = {
                required: rules.required,
                email: rules.email,
            };
        } else if (isRequired(field.key)) {
            field.rules = { required: rules.required };
        } else {
            field.rules = {};
        }
        field.component = component.component;
        field.bind = {
            ...field.bind,
            ...component.bind,
        };

        if (field.errors && field.errors.length) {
            errors.value[field.key] = field.errors;
            delete field.errors;
        }
    });

    const groupedFields = Object.values(fields).reduce((acc, field) => {
        const groupUuid = field.groupUuid || 'base';

        if (!acc[groupUuid]) {
            acc[groupUuid] = {
                uuid: groupUuid,
                name: field.groupName || 'base',
                fields: [],
            };
        }

        acc[groupUuid]['fields'].push(field);
        return acc;
    }, {});

    return Object.values(groupedFields);
}

const requestPending = ref(false);

async function submit() {
    let fieldsToValidate = fields.value.reduce((acc, field) => {
        Object.values(field?.fields ?? {}).forEach((field) => {
            acc[field.key] = field;
        });
        return acc;
    }, []);

    await validate(fieldsToValidate as FieldsType);

    if (Object.keys(errors.value).length) {
        const message =
            'Форма не прошла валидацию, пожалуйста, проверьте введенные данные';
        if (isMguuRespondent.value) {
            emits('validation-error', message);
        } else {
            const { toast } = useToast();
            toast.error(message);
        }
        return;
    }

    requestPending.value = true;

    props.handler(fields.value).finally(() => {
        requestPending.value = false;
    });
}

watch(
    () => props.modelValue,
    (newVal) => {
        fields.value = deepClone(newVal);
        fields.value = modifyFields();
    },
    {
        deep: true,
    },
);

watch(isMguuRespondent, () => {
    fields.value = modifyFields();
});

async function onChange(val, key) {
    if (val instanceof Event) val = val.target?.value ?? '';
    if (val != '') {
        if (key == 'email') {
            if (!validateEmail(val)) {
                errors.value[key] = [
                    { $message: 'Введен неправильный формат e-mail' },
                ];
            } else {
                delete errors.value[key];
            }
        } else {
            delete errors.value[key];
        }
    } else {
        errors.value[key] = [{ $message: 'Поле не заполнено' }];
    }
}
</script>

<template>
    <div class="mguu-form" v-if="isMguuRespondent">
        <main>
            <slot name="prepend-form" />
            <div class="fields-grid">
                <template v-for="group in fields">
                    <template v-for="field in group['fields']">
                        <component
                            :is="field.component"
                            v-bind="field.bind"
                            v-model="field.modelValue"
                            :type="field.type"
                            :required="isRequired(field.key)"
                            @change="(val) => onChange(val, field.key)"
                            :errors="errors[field.key]"
                            :style="
                                field.type === 'radio'
                                    ? { gridColumn: '1 / -1' }
                                    : {}
                            "
                        />
                    </template>
                </template>
            </div>
            <slot name="prepend-buttons" />
        </main>
        <footer>
            <div
                class="footer-left"
                :class="{ 'footer-left_empty': !slots['footer-left'] }"
            >
                <slot name="footer-left" />
            </div>
            <div class="footer-right">
                <div class="footer-right-button-area">
                    <MguuButton
                        :label="props.handlerButtonText"
                        @click="submit"
                        :disabled="props.disableButton"
                    />
                </div>
            </div>
        </footer>
    </div>
    <div class="app-form-builder" v-else>
        <slot name="prepend-form" />
        <app-alert type="info" class="mb-4"
            >Поля со звездочкой <sup>(*)</sup> обязательны для
            заполнения</app-alert
        >
        <div class="app-form-builder__groups">
            <div v-for="group in fields" class="app-form-builder__groups-group">
                <div
                    v-if="group.uuid !== 'base'"
                    class="app-form-builder__groups-group-label"
                >
                    {{ group.name }}
                </div>
                <div class="app-form-builder__groups-group-fields">
                    <template v-for="field in group['fields']">
                        <component
                            :is="field.component"
                            v-bind="field.bind"
                            v-model="field.modelValue"
                            :required="field.required"
                            @change="(val) => onChange(val, field.key)"
                            :errors="errors[field.key]"
                        />
                    </template>
                </div>
            </div>

            <slot name="prepend-buttons" />
            <div class="app-form-builder__btn-wrapper">
                <mt-button
                    @click="submit"
                    :loading="requestPending"
                    :disabled="props.disableButton"
                    class="app-form-builder__btn"
                    size="large"
                >
                    {{ props.handlerButtonText }}
                </mt-button>
            </div>
            <slot name="append-buttons" />
        </div>
    </div>
</template>

<style lang="scss">
.app-form-builder {
    width: 100%;

    &__groups {
        display: flex;
        flex-direction: column;
        gap: 1rem;

        &-group {
            display: flex;
            flex-direction: column;
            gap: 1.5rem;

            &-label {
                color: #7c869d;
            }

            &-fields {
                width: 100%;
                display: flex;
                flex-wrap: wrap;
            }
        }
    }

    &__btn-wrapper {
        margin-top: 20px;
        width: 100%;
    }
}
</style>

<style scoped>
.mguu-form {
    display: flex;
    flex-direction: column;
    height: 100%;

    * {
        box-sizing: border-box;
    }

    main {
        display: flex;
        flex-direction: column;
        gap: 32px;
        width: 100%;
        padding: 32px 36px 20px 36px;
        background-color: rgba(255, 255, 255, 0.7);
        border-radius: 32px 32px 0 0;

        .fields-grid {
            display: grid;
            gap: 32px 20px;
            grid-template-columns: 1fr 1fr;
        }
    }

    footer {
        display: grid;
        grid-template-columns: 1fr 200px;

        .footer-left {
            padding: 30px 36px 32px 36px;
            border-radius: 0 0 32px 32px;
            background-color: rgba(255, 255, 255, 0.7);
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
        }

        .footer-right {
          padding: 0;
          background-color: rgba(255, 255, 255, 0.7);
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          border-radius: 0 0 32px 32px;

          .footer-right-button-area {
              position: relative; 
              padding: 12px 0 0 12px; 
              background-color: #f4f4f5;
              border-radius: 32px 0 0 0; 
              width: 100%;
              box-sizing: border-box;
              display: flex;
              flex-direction: column;
              height: max-content;

              &::before {
                  content: "";
                  position: absolute;
                  bottom: 100%; 
                  right: 0;
                  width: 32px; 
                  height: 32px;
                  background: radial-gradient(circle at top left, transparent 32px, #f4f4f5 32px);
                  pointer-events: none;
              }
          }
      }
    }
}

@media (max-width: 767px) {
    .mguu-form {
        main {
            border-radius: 16px 16px 0 0;
            gap: 20px;
            padding: 20px 20px 0 20px;
            .fields-grid {
                display: grid;
                gap: 20px;
                grid-template-columns: 1fr;
            }
        }
        footer {
            display: flex;
            flex-direction: column;

            .footer-left {
                padding: 0;
                border-radius: 0;
                background-color: transparent;

                &_empty {
                    display: none;
                }
            }

            .footer-right {
                background-color: rgba(255, 255, 255, 0.7);
                border-radius: 0 0 16px 16px;
                display: block;

                .footer-right-button-area {
                    background-color: transparent;
                    padding: 20px 20px 20px 20px;
                    width: 100%;
                    border-radius: 0;

                    button {
                        width: 100%;
                    }
                }

                .footer-right-button-area::before {
                    display: none;
                }
            }
        }
    }
}
</style>
